import { CoverPageTableData } from './coverPageTemplates.util';
import { ProjectLineItemDTO } from '../../../../../project/project.type';
import { ScheduleGraphRowDTO, ScheduleGraphAxisDTO } from '../../../../magic-report/magic-report.type';
import { roundCompletionPercent } from '../../../utils/percent.util';

export interface GraphData {
  percentComplete: number;
  percentRequested: number;
  contingencyUsed: number | null;
  monthsPassed: number;
  scheduleData?: {
    maxMonths: number;
    actualProgress: Array<{ month: number; percent: number; id?: string }>;
    axisStartDate?: string;
    axisEndDate?: string;
    endDateExceeded?: boolean;
    startDateExceeded?: boolean;
  };
  reallocationData?: {
    totalLineItemsReallocated: number;
    currentLineItemsReallocated: number;
    totalBudgetReallocated: number;
    currentBudgetReallocated: number;
  };
}

type ReallocationRow = {
  lineItemId?: string;
  totalReallocation?: number;
  currentReallocation?: number;
  isLastMonth?: boolean;
};

export const SVG_COLORS = {
  primaryBlue: '#1976d2',
  lightBlue: '#90caf9',
  bgGray: '#f5f5f5',
  gridColor: '#e0e0e0',
  textColor: '#333333',
  lightText: '#757575',
};

export const calculateMonthsPassed = (commencementDate: string | null | undefined, periodEndingDate: string | null | undefined): number => {
  if (!commencementDate || !periodEndingDate) return 0;
  try {
    const start = new Date(commencementDate);
    const end = new Date(periodEndingDate);
    if (isNaN(start.getTime()) || isNaN(end.getTime())) return 0;
    const totalMonths = (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth());
    const daysInEndMonth = new Date(end.getFullYear(), end.getMonth() + 1, 0).getDate();
    const partialMonth = (end.getDate() - start.getDate()) / daysInEndMonth;
    return Math.max(0, totalMonths + partialMonth);
  } catch {
    return 0;
  }
};

// Strips any time component so mixed yyyy-MM-dd / ISO datetime strings compare correctly.
const toDateKey = (d: string): string => d.split('T')[0];

/**
 * First of the given dates that is actually filled in. The axis bounds are a strict precedence
 * chain rather than an earliest/latest comparison: the scheduled dates own the border, and the
 * CDCC estimates only step in when the scheduled date they stand in for is missing.
 */
const firstPresent = (...dates: Array<string | null | undefined>): string | null =>
  dates.find((d): d is string => !!d && d.trim() !== '') ?? null;

// ReallocationsRows stores one JSON entry per reallocated line item.
// isLastMonth=true entries are carried over from the previous report.
// When the same lineItemId appears in both, the current-period entry wins.
const parseReallocationRows = (section: CoverPageTableData['reallocationsSection']): ReallocationRow[] => {
  const rowsField = section?.fields?.find(f => f.fieldName === 'ReallocationsRows');
  const allRows = (rowsField?.entries || [])
    .map(e => {
      try {
        return JSON.parse(e.value || '') as ReallocationRow;
      } catch {
        return null;
      }
    })
    .filter((r): r is ReallocationRow => r !== null);

  const seen = new Set<string>();
  const deduped: ReallocationRow[] = [];
  for (const r of [...allRows.filter(r => !r.isLastMonth), ...allRows.filter(r => r.isLastMonth)]) {
    const key = r.lineItemId || '';
    if (!seen.has(key)) {
      seen.add(key);
      deduped.push(r);
    }
  }
  return deduped;
};

// Shared SVG bar renderer used by both bar graph functions
const renderSvgBar = (
  label: string,
  value: number,
  index: number,
  barHeight: number,
  barSpacing: number,
  barWidth: number,
  formatValue: (value: number) => number = Math.round,
): string => {
  const { primaryBlue, bgGray, textColor } = SVG_COLORS;
  const fillWidth = Math.min((value / 100) * barWidth, barWidth);
  const yPos = 45 + index * barSpacing;
  const valueLabelX = fillWidth > 35 ? fillWidth / 2 : barWidth / 2;
  const valueLabelColor = fillWidth > 35 ? 'white' : textColor;
  return `
    <g>
      <text x="0" y="${yPos - 8}" font-family="Times New Roman, serif" font-size="14" fill="${textColor}">${label}</text>
      <rect x="0" y="${yPos}" width="${barWidth}" height="${barHeight}" fill="${bgGray}" rx="2" stroke="#e0e0e0" stroke-width="0.5" />
      <rect x="0" y="${yPos}" width="${fillWidth}" height="${barHeight}" fill="${primaryBlue}" rx="2" />
      <text x="${valueLabelX}" y="${yPos + barHeight / 2 + 3}" font-family="Times New Roman, serif" font-size="13" fill="${valueLabelColor}" font-weight="600" text-anchor="middle">${formatValue(value)}%</text>
    </g>
  `;
};

const renderXAxisLabels = (barWidth: number, yLine: number, yText: number): string => {
  const { lightText } = SVG_COLORS;
  return `
    <line x1="0" y1="${yLine}" x2="${barWidth}" y2="${yLine}" stroke="#e0e0e0" stroke-width="1" />
    <text x="0" y="${yText}" font-family="Times New Roman, serif" font-size="9" fill="${lightText}" text-anchor="start">0%</text>
    <text x="${barWidth * 0.25}" y="${yText}" font-family="Times New Roman, serif" font-size="9" fill="${lightText}" text-anchor="middle">25%</text>
    <text x="${barWidth * 0.5}" y="${yText}" font-family="Times New Roman, serif" font-size="9" fill="${lightText}" text-anchor="middle">50%</text>
    <text x="${barWidth * 0.75}" y="${yText}" font-family="Times New Roman, serif" font-size="9" fill="${lightText}" text-anchor="middle">75%</text>
    <text x="${barWidth}" y="${yText}" font-family="Times New Roman, serif" font-size="9" fill="${lightText}" text-anchor="end">100%</text>
  `;
};

/**
 * Month values to label and tick on the funding-schedule X axis: every other (even) month, widening
 * the step on long schedules so the labels stay readable. The axis end itself is not forced in as a
 * tick — it is the edge of the plot frame.
 */
export const getMonthTicks = (maxMonths: number, maxTicks = 16): number[] => {
  let step = 2;
  while (Math.floor(maxMonths / step) + 1 > maxTicks) step *= 2;
  return Array.from({ length: Math.floor(maxMonths / step) + 1 }, (_, i) => i * step);
};

/**
 * Fixed "Ahead of Schedule" / "Behind Schedule" funnel curves, as fractions (0-1) of the plot
 * rectangle rather than actual funding data — the schedule's month range varies per report, but
 * the funnel shape must always look the same.
 * Each curve passes exactly through its anchor points (measured off the pixels of the client's reference
 * graph on a 12-month axis) using monotone cubic (PCHIP) interpolation, so it stays smooth and never dips.
 * The lower curve is not drawn before its first anchor (x=0.168).
 */
const makeMonotoneCurve = (points: ReadonlyArray<readonly [number, number]>) => {
  const n = points.length;
  const xs = points.map(p => p[0]);
  const ys = points.map(p => p[1]);
  const h = xs.slice(1).map((x, i) => x - xs[i]);
  const d = h.map((hi, i) => (ys[i + 1] - ys[i]) / hi);

  const endSlope = (h0: number, h1: number, d0: number, d1: number) => {
    const m = ((2 * h0 + h1) * d0 - h0 * d1) / (h0 + h1);
    if (Math.sign(m) !== Math.sign(d0)) return 0;
    if (Math.sign(d0) !== Math.sign(d1) && Math.abs(m) > Math.abs(3 * d0)) return 3 * d0;
    return m;
  };

  const m = xs.map((_, k) => {
    if (k === 0) return endSlope(h[0], h[1], d[0], d[1]);
    if (k === n - 1) return endSlope(h[n - 2], h[n - 3], d[n - 2], d[n - 3]);
    if (d[k - 1] * d[k] <= 0) return 0;
    const w1 = 2 * h[k] + h[k - 1];
    const w2 = h[k] + 2 * h[k - 1];
    return (w1 + w2) / (w1 / d[k - 1] + w2 / d[k]);
  });

  return (x: number) => {
    let k = 0;
    while (k < n - 2 && x > xs[k + 1]) k++;
    const t = (x - xs[k]) / h[k];
    const t2 = t * t;
    const t3 = t2 * t;
    return (2 * t3 - 3 * t2 + 1) * ys[k] + (t3 - 2 * t2 + t) * h[k] * m[k] + (-2 * t3 + 3 * t2) * ys[k + 1] + (t3 - t2) * h[k] * m[k + 1];
  };
};

const FUNNEL_UPPER_POINTS = [
  [0, 0.187],
  [0.1, 0.29],
  [0.2, 0.438],
  [0.3, 0.6],
  [0.4, 0.744],
  [0.5, 0.844],
  [0.6, 0.91],
  [0.7, 0.956],
  [0.8, 0.982],
  [0.9, 0.995],
  [1, 1],
] as const;
const FUNNEL_LOWER_POINTS = [
  [0.168, 0.015],
  [0.2, 0.018],
  [0.25, 0.036],
  [0.3, 0.09],
  [0.35, 0.172],
  [0.4167, 0.3],
  [0.5, 0.462],
  [0.6, 0.633],
  [0.7, 0.762],
  [0.8, 0.846],
  [0.9, 0.89],
  [1, 0.918],
] as const;

const FUNNEL_UPPER_FN = makeMonotoneCurve(FUNNEL_UPPER_POINTS);
const FUNNEL_LOWER_FN = makeMonotoneCurve(FUNNEL_LOWER_POINTS);

/** Builds a smooth SVG path for a funnel curve, sampled from xStart to 1 across the plot area. */
export const buildFunnelPath = (
  curveFn: (x: number) => number,
  padding: number,
  plotWidth: number,
  plotHeight: number,
  xStart = 0,
  steps = 40,
): string => {
  const coords = Array.from({ length: steps + 1 }, (_, i) => {
    const x = xStart + ((1 - xStart) * i) / steps;
    const y = curveFn(x);
    return [padding + x * plotWidth, padding + plotHeight - y * plotHeight] as const;
  });
  return coords.map(([x, y], i) => `${i === 0 ? 'M' : 'L'} ${x} ${y}`).join(' ');
};

/**
 * The schedule graph's Y axis is always 0-100%. Letting it grow when a draw runs past 100% squashed
 * the plot and pulled the funnel curves (fractions of the plot) away from their % values; points
 * above 100% are pinned to the top edge instead.
 */
export const SCHEDULE_MAX_PERCENT = 100;

/**
 * Funding Schedule graph geometry (px), shared by the report export and the Schedule section preview so
 * the preview matches the report exactly. Drawn 1:1 — no viewBox scaling — so font sizes are real px.
 */
export const SCHEDULE_GRAPH_LAYOUT = { width: 550, height: 280, padding: 50 };

export const FUNNEL_CURVES = {
  upper: { fn: FUNNEL_UPPER_FN, xStart: FUNNEL_UPPER_POINTS[0][0] },
  lower: { fn: FUNNEL_LOWER_FN, xStart: FUNNEL_LOWER_POINTS[0][0] },
};

/**
 * `completion` carries the Percent Complete / Percent Requested bars. The cover page table resolves
 * them (typed overrides, line-item formulas) and passes them in, so the bars always match the text
 * rows above them instead of re-deriving their own figures.
 */
export const prepareGraphData = (
  data: CoverPageTableData,
  lineItems?: ProjectLineItemDTO[],
  scheduleGraph?: ScheduleGraphRowDTO[],
  graphAxis?: ScheduleGraphAxisDTO,
  completion: Pick<GraphData, 'percentComplete' | 'percentRequested'> = { percentComplete: 0, percentRequested: 0 },
): GraphData => {
  const { budgetSummary, drawSummary, coverPage, scheduleSection, reallocationsSection } = data;

  const { percentComplete, percentRequested } = completion;
  const contractSumToDate = budgetSummary?.contractBudgetSumToDate || 0;
  const contingencyUsed = coverPage?.contingencyPercentageUsed ?? null;

  const commencementDate = scheduleSection?.fields?.find(f => f.fieldName === 'CommencementDate')?.value;
  const cdccEstimatedCommencementDate = scheduleSection?.fields?.find(f => f.fieldName === 'CDCCEstimatedCommencementDate')?.value;
  const cdccEstimatedCompletionDate = scheduleSection?.fields?.find(f => f.fieldName === 'CDCCEstimatedCompletionDate')?.value;
  const revisedCompletionDate = scheduleSection?.fields?.find(f => f.fieldName === 'RevisedCompletionDate')?.value;
  const originalCompletionDate = scheduleSection?.fields?.find(f => f.fieldName === 'OriginalCompletionDate')?.value;

  const graphRows: ScheduleGraphRowDTO[] = scheduleGraph ?? [];

  const allDates = graphRows.map(r => r.applicationDate).filter((d): d is string => !!d);
  const earliestDate = allDates.length > 0 ? allDates.reduce((min, d) => (toDateKey(d) < toDateKey(min) ? d : min)) : null;

  // Axis start: user override → Commencement → CDCC Estimated Commencement → earliest data point
  const axisStartDate = graphAxis?.startDate || firstPresent(commencementDate, cdccEstimatedCommencementDate, earliestDate);
  // Axis end: user override → Revised Completion → Original Completion → CDCC Estimated Completion.
  // With no Revised/Original date on the report, the CDCC estimate is what bounds the graph.
  const axisEndDate = graphAxis?.endDate || firstPresent(revisedCompletionDate, originalCompletionDate, cdccEstimatedCompletionDate);

  const effectiveStart = axisStartDate;

  const validRows = graphRows.filter(r => r.applicationDate != null && r.percentComplete != null);
  const monthsPassed = calculateMonthsPassed(effectiveStart, drawSummary?.periodEnding);

  const historicalPoints = validRows.map(r => ({
    month: calculateMonthsPassed(effectiveStart, r.applicationDate),
    percent: r.percentComplete ?? 0,
    id: r.id,
  }));

  // Commencement is already in historicalPoints if seeded; avoid double-plotting the origin.
  const hasSeededCommencementRow = graphRows.some(r => r.isCommencement);
  const originPoint = { month: 0, percent: 0, id: 'origin' };
  const commencementPointId = graphRows.find(r => r.isCommencement)?.id ?? originPoint.id;
  const actualProgress = [...(hasSeededCommencementRow ? [] : [originPoint]), ...historicalPoints]
    .filter(p => !graphAxis?.hideCommencement || p.id !== commencementPointId)
    .sort((a, b) => a.month - b.month);

  const axisEndMonths = axisEndDate ? calculateMonthsPassed(effectiveStart, axisEndDate) : null;
  const dataMaxMonths = actualProgress.reduce((m, p) => Math.max(m, p.month), 0);
  const endDateExceeded = axisEndMonths != null && dataMaxMonths > axisEndMonths;

  const startDateExceeded =
    !!axisStartDate && validRows.some(r => r.applicationDate != null && toDateKey(r.applicationDate) < toDateKey(axisStartDate));
  // The axis ends exactly on the completion date (or the last data point when funding runs past it)
  // rather than rounding up to the next gridline — an axis that extends beyond the schedule makes the
  // line look like it finishes earlier, and therefore further along, than it actually is.
  const maxMonths = Math.max(1, dataMaxMonths, axisEndMonths ?? 0);
  const totalRevisedBudget = contractSumToDate;

  let reallocationData: GraphData['reallocationData'];
  if (reallocationsSection?.fields) {
    const rows = parseReallocationRows(reallocationsSection);
    const currentRows = rows.filter(r => !r.isLastMonth);
    const totalLineItems = (lineItems || []).filter(li => !li.isDeleted).length;
    const sumTotal = rows.reduce((s, r) => s + Math.abs(r.totalReallocation ?? 0), 0);
    const sumCurrent = currentRows.reduce((s, r) => s + Math.abs(r.currentReallocation ?? 0), 0);
    reallocationData = {
      totalLineItemsReallocated:
        totalLineItems > 0 ? (rows.filter(r => Math.abs(r.totalReallocation ?? 0) > 0.01).length / totalLineItems) * 100 : 0,
      currentLineItemsReallocated:
        totalLineItems > 0 ? (currentRows.filter(r => Math.abs(r.currentReallocation ?? 0) > 0.01).length / totalLineItems) * 100 : 0,
      totalBudgetReallocated: totalRevisedBudget > 0 ? (sumTotal / totalRevisedBudget) * 100 : 0,
      currentBudgetReallocated: totalRevisedBudget > 0 ? (sumCurrent / totalRevisedBudget) * 100 : 0,
    };
  } else {
    reallocationData = {
      totalLineItemsReallocated: 0,
      currentLineItemsReallocated: 0,
      totalBudgetReallocated: 0,
      currentBudgetReallocated: 0,
    };
  }

  return {
    percentComplete,
    percentRequested,
    contingencyUsed,
    monthsPassed,
    scheduleData: {
      maxMonths,
      actualProgress,
      axisStartDate: axisStartDate ?? undefined,
      axisEndDate: axisEndDate ?? undefined,
      endDateExceeded,
      startDateExceeded,
    },
    reallocationData,
  };
};

const renderSvgBarNA = (label: string, index: number, barHeight: number, barSpacing: number, barWidth: number): string => {
  const { bgGray, textColor } = SVG_COLORS;
  const yPos = 45 + index * barSpacing;
  return `
    <g>
      <text x="0" y="${yPos - 8}" font-family="Times New Roman, serif" font-size="14" fill="${textColor}">${label}</text>
      <rect x="0" y="${yPos}" width="${barWidth}" height="${barHeight}" fill="${bgGray}" rx="2" stroke="#e0e0e0" stroke-width="0.5" />
      <text x="${barWidth / 2}" y="${yPos + barHeight / 2 + 3}" font-family="Times New Roman, serif" font-size="13" fill="${textColor}" font-weight="600" text-anchor="middle">N/A</text>
    </g>
  `;
};

export const renderPercentCompleteGraph = (data: GraphData): string => {
  const { percentComplete, percentRequested, contingencyUsed } = data;
  const { textColor } = SVG_COLORS;
  const barWidth = 320;

  return `
    <svg width="396" height="242" viewBox="0 0 360 220" xmlns="http://www.w3.org/2000/svg" style="background-color: white;">
      <text x="0" y="20" font-family="Times New Roman, serif" font-size="16" font-weight="600" fill="${textColor}">Completion</text>
      ${renderSvgBar('Percent Complete', percentComplete, 0, 36, 57, barWidth, roundCompletionPercent)}
      ${renderSvgBar('Percent Requested', percentRequested, 1, 36, 57, barWidth, roundCompletionPercent)}
      ${contingencyUsed === null ? renderSvgBarNA('Contingency Used', 2, 36, 57, barWidth) : renderSvgBar('Contingency Used', contingencyUsed, 2, 36, 57, barWidth)}
      ${renderXAxisLabels(barWidth, 200, 215)}
    </svg>
  `;
};

export const renderScheduleGraph = (data: GraphData): string => {
  if (!data.scheduleData) return '';

  const { maxMonths, actualProgress } = data.scheduleData;
  const { primaryBlue, gridColor, textColor, lightText } = SVG_COLORS;
  const { width: chartWidth, height: chartHeight, padding } = SCHEDULE_GRAPH_LAYOUT;
  const plotWidth = chartWidth - padding * 2;
  const plotHeight = chartHeight - padding * 2;

  const percentTicks = Array.from({ length: SCHEDULE_MAX_PERCENT / 20 + 1 }, (_, i) => i * 20);

  const scaleX = (month: number) => padding + (month / maxMonths) * plotWidth;
  const scaleY = (percent: number) => padding + plotHeight - (Math.min(percent, SCHEDULE_MAX_PERCENT) / SCHEDULE_MAX_PERCENT) * plotHeight;
  const monthTicks = getMonthTicks(maxMonths);
  const axisColor = textColor;

  return `
    <svg width="${chartWidth}" height="${chartHeight}" xmlns="http://www.w3.org/2000/svg" style="background-color: white;">
      <text x="0" y="20" font-family="Times New Roman, serif" font-size="16" font-weight="600" fill="${textColor}">Funding Schedule</text>

      ${percentTicks
        .map(
          p => `
        <line x1="${padding}" y1="${scaleY(p)}" x2="${padding + plotWidth}" y2="${scaleY(p)}" stroke="${gridColor}" stroke-width="1" stroke-dasharray="2,2" />
      `,
        )
        .join('')}

      <rect x="${padding}" y="${padding}" width="${plotWidth}" height="${plotHeight}" fill="none" stroke="${axisColor}" stroke-width="1" />

      <path d="${buildFunnelPath(FUNNEL_CURVES.upper.fn, padding, plotWidth, plotHeight, FUNNEL_CURVES.upper.xStart)}" stroke="${lightText}" stroke-width="1.5" stroke-dasharray="5,4" fill="none" />
      <path d="${buildFunnelPath(FUNNEL_CURVES.lower.fn, padding, plotWidth, plotHeight, FUNNEL_CURVES.lower.xStart)}" stroke="${lightText}" stroke-width="1.5" stroke-dasharray="5,4" fill="none" />

      <path d="${actualProgress.map((p, i) => `${i === 0 ? 'M' : 'L'} ${scaleX(p.month)} ${scaleY(p.percent)}`).join(' ')}" stroke="${primaryBlue}" stroke-width="3" fill="none" stroke-linecap="round" />
      ${actualProgress.map(p => `<circle cx="${scaleX(p.month)}" cy="${scaleY(p.percent)}" r="5" fill="${primaryBlue}" stroke="white" stroke-width="2" />`).join('')}

      <text x="15" y="${chartHeight / 2}" font-family="Times New Roman, serif" font-size="11" fill="${textColor}" text-anchor="middle" transform="rotate(-90, 15, ${chartHeight / 2})">% Funding</text>
      <text x="${chartWidth / 2}" y="${chartHeight - 8}" font-family="Times New Roman, serif" font-size="11" fill="${textColor}" text-anchor="middle">Months</text>

      ${percentTicks
        .map(
          p => `
        <text x="${padding - 8}" y="${scaleY(p) + 4}" font-family="Times New Roman, serif" font-size="9" fill="${lightText}" text-anchor="end">${p}%</text>
      `,
        )
        .join('')}
      ${monthTicks
        .map(
          m => `
        <line x1="${scaleX(m)}" y1="${padding + plotHeight}" x2="${scaleX(m)}" y2="${padding + plotHeight + 4}" stroke="${axisColor}" stroke-width="1" />
        <text x="${scaleX(m)}" y="${chartHeight - 20}" font-family="Times New Roman, serif" font-size="9" fill="${lightText}" text-anchor="middle">${m}</text>
      `,
        )
        .join('')}

      <text x="${padding + 10}" y="${padding + 15}" font-family="Times New Roman, serif" font-size="10" fill="${textColor}" font-weight="500" opacity="0.7">Ahead of Schedule</text>
      <text x="${padding + plotWidth - 95}" y="${padding + plotHeight - 10}" font-family="Times New Roman, serif" font-size="10" fill="${textColor}" font-weight="500" opacity="0.7">Behind Schedule</text>
    </svg>
  `;
};

export const renderReallocationGraph = (data: GraphData): string => {
  if (!data.reallocationData) return '';

  const { totalLineItemsReallocated, currentLineItemsReallocated, totalBudgetReallocated, currentBudgetReallocated } =
    data.reallocationData;
  const { textColor } = SVG_COLORS;
  const barWidth = 320;

  return `
    <svg width="396" height="242" viewBox="0 0 360 220" xmlns="http://www.w3.org/2000/svg" style="background-color: white;">
      <text x="0" y="20" font-family="Times New Roman, serif" font-size="16" font-weight="600" fill="${textColor}">Reallocations</text>
      ${renderSvgBar('Total % of Line Items Reallocated', totalLineItemsReallocated, 0, 22, 42, barWidth)}
      ${renderSvgBar('Current % of Line Items Reallocated', currentLineItemsReallocated, 1, 22, 42, barWidth)}
      ${renderSvgBar('Total % of Budget Reallocated', totalBudgetReallocated, 2, 22, 42, barWidth)}
      ${renderSvgBar('Current % of Budget Reallocated', currentBudgetReallocated, 3, 22, 42, barWidth)}
      ${renderXAxisLabels(barWidth, 200, 215)}
    </svg>
  `;
};
